import {DxNavigationTreeNode} from "./dx-navigation-tree-node";
import {DxNavigationTreeNodeDataProvider} from "./dx-navigation-tree-node-data-provider";


/**
 * Внутренняя структура данных навигатора.
 * Представляет собой типовое дерево навигационных узлов.
 * Используется как в вехнем навигационном меню так и в нижнем.
 * @publicApi
 */
export class DxNavigationTree {
  /**
   * Исходная плоская иерархия, переданная дереву.
   */
  private readonly _data: DxNavigationTreeNodeDataProvider[];

  /**
   * Фиктивный корень дерева.
   * @name _root
   * @type {DxNavigationTreeNode}
   */
  private readonly _root: DxNavigationTreeNode;

  /**
   * Индекс дерева. Мапа айдишников и ссылок на узлы.
   * Для быстрого поиска и итерирования по узлам в совокупности.
   * @name _index
   * @type {Map<number, DxNavigationTreeNode>}
   */
  private _index: Map<number, DxNavigationTreeNode>;


  /**
   * Создает навигационное дерево.
   * Конструктор дерева принимает массив любых объектов, реализующх интерфэйс DxNavigationTreeNodeDataProvider, которые
   * образуют плосткую иерархии через id и parentId.
   *
   * @constructor
   * @param {DxNavigationTreeNodeDataProvider[]} data - плоская иерархия объектов для создания дерева.
   */
  constructor(data: DxNavigationTreeNodeDataProvider[]) {
    this._data = data;
    this._root = new DxNavigationTreeNode(null);
    this._root.id = DxNavigationTreeNode.generateNodeId();
    this._root.ident = 'DX_NAVIGATION_TREE_ROOT';
    this._root.title = 'ROOT';
    this._root.level = 0;
    this._index = new Map();
  }


  /**
   * Вызывает рост дерева из плоской иерархии.
   * @param {string} key - поле группировки объектов.
   */
  public grow(key: string): void {
    // Группируем плоскую иерархию по переданному ключу
    if(!this._data) return;
    const groups = DxNavigationTree.groupBy(this._data, key);

    // Получаем первый уровень дерева - ноды без без родителя.
    const parents: DxNavigationTreeNodeDataProvider[] = groups['undefined'];
    // Рекурсивно строим дерево
    let node: DxNavigationTreeNode;
    for(let i = 0; i < parents.length; i++) {
      node = this._createNode(groups, this._root, parents[i]);
      this._index.set(node.id, node);
      this._root.children.push(node);
    }
  }


  private _createNode(groups: any, parent: DxNavigationTreeNode, nodeData: DxNavigationTreeNodeDataProvider): DxNavigationTreeNode {
    const node: DxNavigationTreeNode = new DxNavigationTreeNode(parent, nodeData);
    const children: DxNavigationTreeNodeDataProvider[] = groups[node.id];

    if(children && children.length > 0) {
      let childNode: DxNavigationTreeNode;
      for(let i = 0; i < children.length; i++) {
        childNode = this._createNode(groups, node, children[i]);
        this._index.set(childNode.id, childNode);
        node.children.push(childNode);
      }
    }

    this._index.set(node.id, node);
    return node;
  }

  /** Вычисляет максимальную текущую глубину дерева. */
  public get depth(): number {
    const leafLevels: number[] = [];
    this._index.forEach((value, key) => {
      if(value.isLeaf) {
        leafLevels.push(value.level);
      }
    });
    return Math.max(...leafLevels);
  }

  /**
   * Возвращает массив узлов дерева заданого уровня.
   * @param {number} level - целевой уровень дерева.
   * @return {DxNavigationTreeNode[]} - узлов дерева заданого уровня. Пустой массив если нодов с таким уровнем нет.
   */
  public collectNodesByLevel(level: number): DxNavigationTreeNode[] {
    const nodes: DxNavigationTreeNode[] = [];
    this._index.forEach((value, key) => {
      if(value.level === level) {
        nodes.push(value);
      }
    });
    return nodes;
  }

  public findNodesByRoutes(routes: string[]): DxNavigationTreeNode[] {
    const nodes: DxNavigationTreeNode[] = [];
    this._index.forEach((value, key) => {
      if(routes.indexOf(value.route) !== -1) {
        nodes.push(value);
      }
    });
    return nodes;
  }

  /**
   * Немного магии за пределами Хогвардс :)
   * Строит группировку массива JSON-объектов по некоторому полю.
   * @param {any} xs - массив JSON-объектов.
   * @param {string} key - имя атрибута, по которому должна строится группировка.
   * @return {any} - JSON-объект с группами.
   */
  public static groupBy(xs, key) {
    return xs.reduce(function (rv, x) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  }
}
